/** @format */

import {
	AppProductReviewState,
	FilterType,
	InvalidResponse,
	ProductReviewAnalyticsService,
	ReviewFilter,
	SelectedProductReviewsProsCons,
	addReviewFilter,
	addSortFilter,
	cleanSelectedTag,
	removeReviewFilter,
	removeSortFilter,
	selectProductReviewDrawer,
	selectReviewFilters,
	selectSelectedTag,
	setRatingFilter,
	setSelectedFeature
} from '@product-reviews/data-access';
import {
	ChangeDetectorRef,
	Component,
	computed,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges
} from '@angular/core';
import { DropdownSelect, SelectedFilter, SelectedFilterType, SortOptionKey } from '../models';
import { RATING_DROPDOWN_OPTIONS, SORT_DROPDOWN_OPTIONS } from '../constants';
import { Subject, filter, map, takeUntil } from 'rxjs';

import { BreakPointNew } from '@shared/data-access/enums';
import { Store } from '@ngrx/store';
import { BreakpointsService } from '@shared/data-access/services';
import { ProductReviewProsConsType } from 'libs/product-reviews/data-access/src/lib/model/product-review-pros-cons.enum';
import { CommonModule } from '@angular/common';
import { ReviewDropdownSelectComponent } from '../review-dropdown-select/review-dropdown-select.component';
import { ReviewFilterChipsComponent } from '../review-filter-chips/review-filter-chips.component';
import { ReviewSearchInputComponent } from '../review-search-input/review-search-input.component';
import { ReviewToggleSelectComponent } from '../review-toggle-select/review-toggle-select.component';

@Component({
	selector: 'product-reviews-filter-drawer',
	templateUrl: './review-filter-drawer.component.html',
	styleUrl: './review-filter-drawer.component.scss',
	imports: [
		CommonModule,
		ReviewDropdownSelectComponent,
		ReviewFilterChipsComponent,
		ReviewSearchInputComponent,
		ReviewToggleSelectComponent
	]
})
export class ReviewFilterDrawerComponent implements OnInit, OnChanges, OnDestroy {
	@Input() hasSearched = false;
	@Input() reviewCount = 0;
	@Input() reviewsFilters = false;
	@Input() reviewsProsCons = false;
	@Input() isHighlyRatedReviewsEnabled = false;

	private readonly store = inject(Store<AppProductReviewState>);
	private readonly analyticsService = inject(ProductReviewAnalyticsService);
	private readonly breakpointsService = inject(BreakpointsService);
	private readonly cdRef = inject(ChangeDetectorRef);

	private readonly UNSUBSCRIBE$ = new Subject<void>();
	isMobile = computed(() => this.breakpointsService.breakpoint() <= BreakPointNew.MEDIUM);
	filterTypes = SelectedFilterType;
	sortOptions = SORT_DROPDOWN_OPTIONS;
	ratingOptions = structuredClone(RATING_DROPDOWN_OPTIONS);
	labelSort = 'Sort';
	labelImageReviews = 'Reviews With Images or Videos';
	labelVerifiedPurchases = 'Verified Purchases';
	isMediaSelected: boolean;
	isVerifiedPurchaserSelected: boolean;
	isDropdownOpen = false;
	storeSelectorDrawer$ = this.store.select(selectProductReviewDrawer);
	selectedFilters$ = this.store.select(selectReviewFilters);
	filters: ReviewFilter[] = [];
	selectedTag$ = this.store.select(selectSelectedTag);
	trigger = false;
	triggerSortDefault = true;

	ngOnInit(): void {
		this.setupDrawerStore();
		this.getReviewFilters();
	}

	ngOnChanges(changes: SimpleChanges): void {
		const { previousValue, currentValue } = changes.hasSearched || {};

		if (!previousValue && currentValue) {
			this.sortOptions = [...SORT_DROPDOWN_OPTIONS];

			const mostRelevant = this.sortOptions.find(
				(sort) => sort.key === SortOptionKey.MOST_RELEVANT
			);

			this.store.dispatch(removeSortFilter());
			this.labelSort = mostRelevant.label;
			this.triggerSortDefault = true;
		} else if (previousValue && !currentValue) {
			this.labelSort = 'Sort';
		}
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	handleToggleSelector(data: SelectedFilter) {
		switch (data.name) {
			case this.filterTypes.HAS_MEDIA:
				this.isMediaSelected = !!data.value;
				this.handleFilterSelected(data, this.labelImageReviews, this.isMediaSelected);
				return;
			case this.filterTypes.VERIFIED_PURCHASER:
				this.isVerifiedPurchaserSelected = !!data.value;
				this.handleFilterSelected(
					data,
					this.labelVerifiedPurchases,
					this.isVerifiedPurchaserSelected
				);
				return;
		}
	}

	updateSortOptions(options: DropdownSelect[]) {
		let updatedOptions = [...options];
		if (updatedOptions.every(({ selected }) => !selected)) {
			updatedOptions = [...SORT_DROPDOWN_OPTIONS];

			this.triggerSortDefault = true;
		}

		this.sortOptions = updatedOptions;
	}

	getSelectedSortOption({ label, selected, type, order }: Partial<DropdownSelect>) {
		if (!selected) {
			this.store.dispatch(removeSortFilter());
			this.labelSort = 'Sort';
			return;
		}

		this.labelSort = label;
		const sortId = `${type}-${order}`;

		const sortPayload: ReviewFilter = {
			id: sortId,
			label,
			name: type,
			type: FilterType.SORT,
			value: order
		};

		this.store.dispatch(addSortFilter({ sort: sortPayload }));
		this.trackFilterEvent(sortId);
	}

	getSelectedRatingOption({ label, key, selected }: DropdownSelect) {
		const index = this.ratingOptions.findIndex((option) => option.key === key);
		if (index !== -1) {
			this.ratingOptions[index].selected = selected;
		}

		this.store.dispatch(
			setRatingFilter({
				rating: { label, key, selected }
			})
		);

		if (selected) {
			this.trackFilterEvent(`${SelectedFilterType.RATING} +${key}`);
		}
	}

	updatedRatingOptions(options: DropdownSelect[]) {
		this.ratingOptions = options;
	}

	handleDropdownEvent(isDropdownOpen: boolean) {
		this.isDropdownOpen = isDropdownOpen;
	}

	private handleFilterSelected({ name, value }: SelectedFilter, label = '', isSelected = false) {
		if (!isSelected) {
			this.store.dispatch(removeReviewFilter({ filterId: name }));
			return;
		}

		this.trackFilterEvent(`${name}:${value}`);

		this.store.dispatch(
			addReviewFilter({
				filter: {
					id: name,
					label,
					name,
					type: FilterType.STANDARD,
					value
				}
			})
		);
	}

	private setupDrawerStore() {
		this.storeSelectorDrawer$
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				filter((data) => !(data instanceof InvalidResponse))
			)
			.subscribe(({ open }) => {
				if (open) {
					return;
				}

				this.isMediaSelected = false;
				this.isVerifiedPurchaserSelected = false;

				this.sortOptions = [...SORT_DROPDOWN_OPTIONS];

				this.ratingOptions = structuredClone(RATING_DROPDOWN_OPTIONS);
			});
	}

	private getReviewFilters(): void {
		this.selectedFilters$
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				map((filters) => (filters instanceof InvalidResponse ? [] : filters))
			)
			.subscribe((filters: ReviewFilter[]) => {
				this.filters = filters;

				if (this.isHighlyRatedReviewsEnabled) {
					this.updateRatingOptionsFromStore(filters);
				}
			});
	}

	updateRatingOptionsFromStore(filters: ReviewFilter[]) {
		const ratingOptions = structuredClone(RATING_DROPDOWN_OPTIONS);
		filters.forEach((filterItem) => {
			if (filterItem.type === FilterType.RATING) {
				const ratingOptionIndex = ratingOptions.findIndex(
					(op) => op.key === filterItem.value
				);
				if (ratingOptionIndex > -1) {
					ratingOptions[ratingOptionIndex] = {
						key: filterItem.value as string,
						label: filterItem.label,
						selected: true
					};
				}
			}
		});

		this.updatedRatingOptions(ratingOptions);
	}

	private trackFilterEvent(detail: string) {
		const payload = {
			event: 'RatingsAndReviewsInteractions',
			value: 'Filter',
			details: detail
		};

		this.analyticsService.fireReviewAnalytics<{ [key: string]: string }>({
			name: 'multiUseAnalyticsEvent',
			payload
		});
	}

	// eslint-disable-next-line @typescript-eslint/no-shadow
	handleClearFilter(filter: ReviewFilter | SelectedProductReviewsProsCons): void {
		if (this.isHighlyRatedReviewsEnabled && 'feature' in filter) {
			const isProCon = [
				ProductReviewProsConsType.PRO,
				ProductReviewProsConsType.CON
			].includes(filter.type);
			if (isProCon) {
				this.store.dispatch(setSelectedFeature({ selectedFeature: null }));
				return;
			}
		}

		switch (filter.type) {
			case FilterType.STANDARD: {
				const filterSelected = { name: filter.name, value: false };
				this.handleToggleSelector(filterSelected);
				break;
			}
			case FilterType.TAG:
				this.unselectTag(filter);
				break;
			case FilterType.RATING: {
				const dropdownSelected = {
					key: filter.value as string,
					label: filter.label,
					selected: false
				};

				this.getSelectedRatingOption(dropdownSelected);
				this.trigger = true;
				break;
			}
		}
	}

	handleClearAllFilters(): void {
		this.unselectAllToggles();
		this.unselectAllRatingOptions();
		this.store.dispatch(cleanSelectedTag());
		this.store.dispatch(setSelectedFeature({ selectedFeature: null }));
	}

	unselectAllToggles(): void {
		for (const currentFilter of this.filters) {
			if (currentFilter.name === this.filterTypes.HAS_MEDIA) {
				this.isMediaSelected = false;
			}
			if (currentFilter.name === this.filterTypes.VERIFIED_PURCHASER) {
				this.isVerifiedPurchaserSelected = false;
			}
		}
	}

	unselectAllRatingOptions(): void {
		for (const option of this.ratingOptions) {
			option.selected = false;
		}
		this.trigger = true;
	}

	// eslint-disable-next-line @typescript-eslint/no-shadow
	unselectTag(filter: ReviewFilter): void {
		this.store.dispatch(removeReviewFilter({ filterId: filter.id }));
	}

	handleWasDefault(): void {
		this.trigger = false;
	}

	handleWasSortDefault(): void {
		this.triggerSortDefault = false;
		this.cdRef.detectChanges();
	}
}
